import dayjs, { Dayjs } from 'dayjs'
import duration from 'dayjs/plugin/duration'
import relativeTime from 'dayjs/plugin/relativeTime'

// Extend dayjs with required plugins
dayjs.extend(relativeTime)
dayjs.extend(duration)

export interface TimeDifference {
  hours: number
  days: number
  months: number
  years: number
  totalHours: number
  totalDays: number
  totalMonths: number
  totalYears: number
}

export interface TimeDifferenceOptions {
  precision?: 'hours' | 'days' | 'months' | 'years'
  includeTime?: boolean
}

/**
 * Get time difference between two dates in various units
 * @param fromDate - The starting date
 * @param toDate - The ending date (defaults to now)
 * @param options - Configuration options
 * @returns Object with time differences in different units
 */
export function getTimeDifference(
  fromDate: Date | string | Dayjs,
  toDate: Date | string | Dayjs = new Date(),
): TimeDifference {
  const from = dayjs(fromDate)
  const to = dayjs(toDate)

  if (!from.isValid() || !to.isValid()) {
    throw new Error('Invalid date provided')
  }

  const diff = to.diff(from)
  const duration = dayjs.duration(diff)

  return {
    hours: Math.floor(duration.asHours()) % 24,
    days: Math.floor(duration.asDays()) % 30,
    months: Math.floor(duration.asMonths()) % 12,
    years: Math.floor(duration.asYears()),
    totalHours: Math.floor(duration.asHours()),
    totalDays: Math.floor(duration.asDays()),
    totalMonths: Math.floor(duration.asMonths()),
    totalYears: Math.floor(duration.asYears()),
  }
}

/**
 * Get formatted time difference string
 * @param fromDate - The starting date
 * @param toDate - The ending date (defaults to now)
 * @returns Formatted string with time difference
 */
export function getFormattedTimeDifference(
  fromDate: Date | string | Dayjs,
  toDate: Date | string | Dayjs = new Date(),
  options: TimeDifferenceOptions = {},
): string {
  const { precision = 'days', includeTime = false } = options
  const diff = getTimeDifference(fromDate, toDate)

  const parts: string[] = []

  if (diff.years > 0) {
    parts.push(`${diff.years} year${diff.years !== 1 ? 's' : ''}`)
  }

  if (diff.months > 0 && precision !== 'years') {
    parts.push(`${diff.months} month${diff.months !== 1 ? 's' : ''}`)
  }

  if (diff.days > 0 && precision !== 'years' && precision !== 'months') {
    parts.push(`${diff.days} day${diff.days !== 1 ? 's' : ''}`)
  }

  if (diff.hours > 0 && precision === 'hours') {
    parts.push(`${diff.hours} hour${diff.hours !== 1 ? 's' : ''}`)
  }

  if (parts.length === 0) {
    return 'Just now'
  }

  const timeStr = parts.join(', ')
  const direction = dayjs(fromDate).isBefore(dayjs(toDate)) ? 'ago' : 'from now'

  return `${timeStr} ${direction}`
}

/**
 * Get time difference in a more readable format with smart unit selection
 * @param fromDate - The starting date
 * @param toDate - The ending date (defaults to now)
 * @returns Smart formatted time difference string
 */
export function getSmartTimeDifference(
  fromDate: Date | string | Dayjs,
  toDate: Date | string | Dayjs = new Date(),
): string {
  const diff = getTimeDifference(fromDate, toDate)

  if (diff.totalYears >= 1) {
    return getFormattedTimeDifference(fromDate, toDate, { precision: 'years' })
  } else if (diff.totalMonths >= 1) {
    return getFormattedTimeDifference(fromDate, toDate, { precision: 'months' })
  } else if (diff.totalDays >= 1) {
    return getFormattedTimeDifference(fromDate, toDate, { precision: 'days' })
  } else {
    return getFormattedTimeDifference(fromDate, toDate, { precision: 'hours' })
  }
}
